"use client";

import Image from "next/image";
import { useVoyageStore } from "@/hooks/use-voyage-store";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Calendar, MapPin } from "lucide-react";
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Skeleton } from "../ui/skeleton";

export default function GalleryClient() {
  const { photos, deletePhoto, isInitialized } = useVoyageStore();

  if (!isInitialized) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="flex flex-col">
            <Skeleton className="aspect-video w-full" />
            <CardHeader>
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </CardHeader>
            <CardFooter>
               <Skeleton className="h-10 w-24" />
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-20 border-2 border-dashed rounded-lg">
        <h2 className="text-xl font-semibold font-headline">Your Gallery is Empty</h2>
        <p className="text-muted-foreground mt-2">Sail the high seas and take some photos to see them here!</p>
        <Button asChild className="mt-4">
          <a href="/">Start Your Voyage</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
      {photos.map(photo => (
        <Card key={photo.id} className="flex flex-col shadow-lg hover:shadow-xl transition-shadow duration-300">
          <CardContent className="p-0">
            <div className="aspect-video relative">
              <Image 
                src={photo.imageUrl} 
                alt={`Photo of ${photo.locationName}`} 
                fill 
                className="rounded-t-lg object-cover"
                data-ai-hint={photo.hint ?? 'landscape'}
              />
            </div>
          </CardContent>
          <CardHeader className="flex-1">
            <CardTitle className="font-headline">{photo.locationName}</CardTitle>
            <div className="text-sm text-muted-foreground flex items-center gap-4 pt-1">
                <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    <span>{format(new Date(photo.timestamp), 'MMM d, yyyy')}</span>
                </div>
            </div>
          </CardHeader>
          <CardFooter>
             <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your photograph of {photo.locationName}.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => deletePhoto(photo.id)}>
                    Yes, delete it
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}
